import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

const LETTER_REGEX = /^[a-zA-ZçÇğĞıİöÖşŞüÜ\s]+$/;
const NUMBER_REGEX = /^\d+$/;

const Input = forwardRef(
  (
    {
      type = "text",
      label,
      error,
      required = false,
      className = "",
      inputClassName: inputClassNameProp = "",
      onFocus: propOnFocus,
      onBlur: propOnBlur,
      onChange: propOnChange,
      defaultValue,
      value: controlledValue,

      onlyLetter = false,
      onlyNumber = false,

      showAppearance = false,
      leftIcon,
      rightIcon,
      onLeftIconClick,
      onRightIconClick,
      rightCheckbox,
      onRightCheckboxChange,
      isFloating = false,
      ...props
    },
    ref,
  ) => {
    const { t } = useTranslationDetector();
    const [showPassword, setShowPassword] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    const [hasInputValue, setHasInputValue] = useState(() => {
      const initialValue =
        controlledValue !== undefined ? controlledValue : defaultValue;
      return initialValue !== undefined && String(initialValue).length > 0;
    });

    useEffect(() => {
      if (controlledValue !== undefined) {
        setHasInputValue(String(controlledValue || "").length > 0);
      }
    }, [controlledValue]);

    // Sync hasInputValue with ref value for uncontrolled inputs (e.g., after reset())
    useEffect(() => {
      if (controlledValue === undefined && internalRef.current) {
        const currentValue = internalRef.current.value || "";
        setHasInputValue(currentValue.length > 0);
      }
    });

    const internalRef = useRef(null);
    useImperativeHandle(ref, () => internalRef.current);

    const isPassword = type === "password";
    const inputType = isPassword && showPassword ? "text" : type;

    const hasPlaceholder = props.placeholder && props.placeholder.length > 0;
    const isDateType =
      type === "date" || type === "datetime-local" || type === "time";

    const shouldHideAppearance =
      !showAppearance &&
      (type === "number" ||
        type === "date" ||
        type === "time" ||
        type === "datetime-local");

    const showLeftIcon = leftIcon && !isPassword && !showAppearance;
    const showRightIcon = rightIcon && !isPassword && !showAppearance;
    const showRightCheckbox = rightCheckbox && !isPassword && !showAppearance;

    const shouldFloat =
      isFocused ||
      hasInputValue ||
      hasPlaceholder ||
      isDateType ||
      props.disabled ||
      isFloating;

    const handleFocus = useCallback(
      (e) => {
        setIsFocused(true);
        propOnFocus?.(e);
      },
      [propOnFocus],
    );

    const handleBlur = useCallback(
      (e) => {
        setIsFocused(false);

        const inputValue = internalRef?.current?.value || "";
        setHasInputValue(inputValue.length > 0);

        propOnBlur?.(e);
      },
      [propOnBlur],
    );

    const handleChange = useCallback(
      (e) => {
        setHasInputValue(e.target.value.length > 0);
        propOnChange?.(e);
      },
      [propOnChange],
    );

    const togglePassword = useCallback(() => {
      setShowPassword((prev) => !prev);
    }, []);

    const handleKeyDown = useCallback(
      (e) => {
        const key = e.key;

        if (e.ctrlKey || e.metaKey || e.altKey) {
          props.onKeyDown?.(e);
          return;
        }

        if (
          key === "Backspace" ||
          key === "Tab" ||
          key === "ArrowLeft" ||
          key === "ArrowRight" ||
          key === "Delete"
        ) {
          props.onKeyDown?.(e);
          return;
        }

        if (onlyNumber && !NUMBER_REGEX.test(key)) {
          e.preventDefault();
          return;
        }

        if (onlyLetter && !LETTER_REGEX.test(key)) {
          e.preventDefault();
          return;
        }

        props.onKeyDown?.(e);
      },
      [onlyLetter, onlyNumber, props],
    );

    const handlePaste = useCallback(
      (e) => {
        const pasted = e.clipboardData.getData("text");

        if (onlyNumber && !NUMBER_REGEX.test(pasted)) {
          e.preventDefault();
          return;
        }

        if (onlyLetter && !LETTER_REGEX.test(pasted)) {
          e.preventDefault();
          return;
        }

        props.onPaste?.(e);
      },
      [onlyLetter, onlyNumber, props],
    );

    const inputClassName = useCallback(
      () =>
        clsx(
          "peer h-[56px] w-full rounded-md border p-4 font-medium focus:ring-2 focus:outline-none",
          error
            ? "focus:border-danger focus:ring-danger border-danger"
            : "focus:border-primary focus:ring-primary border-border",
          props.disabled && "bg-background-global cursor-not-allowed",
          isPassword && "pr-12",
          showLeftIcon && "pl-12",
          showRightIcon && "pr-12",
          showRightCheckbox && "pr-12",
          label && "pt-4",
          shouldHideAppearance && [
            "appearance-none",
            "[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none",
            "[&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:opacity-0",
            "[&::-webkit-clear-button]:hidden [&::-webkit-clear-button]:appearance-none",
            "[&::-ms-clear]:hidden [&::-ms-reveal]:hidden",
          ],
        ),
      [
        error,
        props.disabled,
        isPassword,
        label,
        shouldHideAppearance,
        showLeftIcon,
        showRightIcon,
        showRightCheckbox,
      ],
    );

    const labelClassName = useCallback(
      () =>
        clsx(
          "pointer-events-none absolute flex items-center gap-1 transition-all duration-200 ltr:left-4 rtl:right-4",
          !props.disabled && "bg-white",
          shouldFloat
            ? "-top-2 px-2 text-xs"
            : "top-1/2 -translate-y-1/2 text-base",
          error
            ? "text-danger"
            : isFocused
              ? "text-primary"
              : "text-foreground-muted",
          props.disabled && "text-gray-400",
        ),
      [shouldFloat, error, props.disabled, isFocused],
    );

    return (
      <div className={clsx("flex flex-col gap-2", className)}>
        <div className="relative">
          <input
            type={inputType}
            ref={internalRef}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            {...(controlledValue !== undefined
              ? { value: controlledValue || "" }
              : { defaultValue })}
            autoComplete="off"
            className={clsx(inputClassName(), inputClassNameProp)}
            {...props}
          />

          {label && (
            <label htmlFor={props.id} className={labelClassName()}>
              {props.disabled && (
                <span className="disabled-line absolute top-1/2 left-0 block h-px w-full bg-white"></span>
              )}
              <span className="relative z-[1]">{label}</span>
            </label>
          )}

          {isPassword && (
            <button
              type="button"
              onClick={togglePassword}
              className="absolute top-1/2 -translate-y-1/2 cursor-pointer text-gray-500 transition-colors hover:text-gray-700 focus:text-gray-700 focus:outline-none ltr:right-3 rtl:left-3"
              disabled={props.disabled}
              tabIndex={-1}
            >
              <FontAwesomeIcon
                icon={
                  !showPassword
                    ? byPrefixAndName.fal["eye-slash"]
                    : byPrefixAndName.fal["eye"]
                }
                className="h-4 w-4"
              />
            </button>
          )}

          {showLeftIcon && (
            <div
              className="text-foreground-muted hover:text-foreground focus:text-primary absolute top-1/2 left-3 -translate-y-1/2 cursor-pointer transition-colors"
              onClick={onLeftIconClick}
              tabIndex={-1}
            >
              {leftIcon}
            </div>
          )}

          {showRightIcon && (
            <div
              className="text-foreground-muted hover:text-foreground focus:text-primary absolute top-1/2 right-3 -translate-y-1/2 cursor-pointer transition-colors"
              onClick={onRightIconClick}
              tabIndex={-1}
            >
              {rightIcon}
            </div>
          )}

          {showRightCheckbox && (
            <div className="absolute top-1/2 -translate-y-1/2 ltr:right-3 rtl:left-3">
              {rightCheckbox}
            </div>
          )}
        </div>

        {error && (
          <p className="text-danger flex items-center gap-1 pl-4 text-xs">
            <FontAwesomeIcon icon={byPrefixAndName.far["circle-exclamation"]} />
            <span>
              {typeof error === "string" ? error : t("common.required")}
            </span>
          </p>
        )}
      </div>
    );
  },
);

Input.displayName = "Input";

const MemoizedInput = memo(Input);
MemoizedInput.displayName = "Input";

export default MemoizedInput;
